import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, XCircle } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { LoadingScreen } from "@/components/ProtectedRoute";
import { NovaLogo } from "@/components/landing/Logo";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { API } = useAuth();
  const navigate = useNavigate();
  const [tokenInfo, setTokenInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/auth/reset-password/${encodeURIComponent(token)}`)
      .then(({ data }) => { if (active) setTokenInfo(data); })
      .catch((err) => { if (active) setError(formatApiErrorDetail(err.response?.data?.detail)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [API, token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      navigate("/login?reset=success", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen label="Checking your reset link…" />;

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="reset-password-page">
      <div className="hidden lg:flex relative flex-col justify-between bg-nova-navy text-white p-12 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full bg-nova-blue/20 blur-3xl" />
        <div className="absolute inset-0 bg-grid-navy opacity-[0.15]" />
        <div className="relative"><NovaLogo inverted /></div>
        <p className="relative text-[10px] font-semibold tracking-[0.32em] text-blue-400">A NEW WAY FORWARD.</p>
      </div>

      <div className="flex items-center justify-center px-5 sm:px-10 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-md">
          <Link to="/login" data-testid="reset-password-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-nova-muted hover:text-nova-navy transition-colors mb-8">
            <ArrowLeft size={15} /> Back to sign in
          </Link>

          {!tokenInfo ? (
            <div className="rounded-3xl border border-red-100 bg-red-50 p-8" data-testid="reset-password-invalid-message">
              <XCircle size={34} className="text-red-500" />
              <h1 className="mt-5 text-2xl font-bold tracking-tighter text-nova-navy">Link unavailable</h1>
              <p className="mt-3 text-sm text-red-700 leading-relaxed">{error}</p>
              <Link to="/forgot-password" data-testid="reset-password-retry-link" className="mt-7 inline-flex items-center gap-2 rounded-full bg-nova-blue px-6 py-3 text-sm font-semibold text-white hover:bg-nova-bluedark transition-colors duration-300">
                Request a new link <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-bold tracking-tighter text-nova-navy">Choose a new password</h1>
              <p className="mt-2 text-sm text-nova-slate">
                Resetting password for <span className="font-semibold text-nova-navy" data-testid="reset-password-email">{tokenInfo.email}</span>.
              </p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="reset-password-form">
                <div>
                  <label htmlFor="reset-password-input" className="block text-xs font-semibold text-nova-navy mb-1.5">
                    New password <span className="text-nova-muted font-normal">(8+ characters)</span>
                  </label>
                  <input
                    id="reset-password-input"
                    data-testid="reset-password-password-input"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                  />
                </div>
                <div>
                  <label htmlFor="reset-password-confirm-input" className="block text-xs font-semibold text-nova-navy mb-1.5">Confirm new password</label>
                  <input
                    id="reset-password-confirm-input"
                    data-testid="reset-password-confirm-input"
                    type="password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
                  />
                </div>

                {error && (
                  <p data-testid="reset-password-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>
                )}

                <motion.button
                  type="submit"
                  disabled={submitting}
                  data-testid="reset-password-submit-btn"
                  whileHover={{ scale: submitting ? 1 : 1.02 }}
                  whileTap={{ scale: submitting ? 1 : 0.98 }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-nova-blue px-6 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300 disabled:opacity-60"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  Reset password
                  {!submitting && <ArrowRight size={15} />}
                </motion.button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
